
import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, X } from "lucide-react";

interface Alert {
  id: string;
  text: string;
  is_active: boolean;
}

interface AlertControlProps {
  alerts: Alert[];
  onAddAlert: (text: string) => void;
  onRemoveAlert: (id: string) => void;
  onUpdateAlert: (id: string, text: string) => void;
  onToggleActive: (id: string, isActive: boolean) => void;
}

export const AlertControl: React.FC<AlertControlProps> = ({
  alerts,
  onAddAlert,
  onRemoveAlert,
  onUpdateAlert,
  onToggleActive,
}) => {
  const [newAlert, setNewAlert] = React.useState("");

  const handleAddAlert = () => {
    if (newAlert.trim()) {
      onAddAlert(newAlert);
      setNewAlert("");
    }
  };

  return (
    <Card className="p-4 space-y-4 bg-ticker-bg text-ticker-text">
      <div className="flex gap-2">
        <Input
          value={newAlert}
          onChange={(e) => setNewAlert(e.target.value)}
          placeholder="Enter new alert"
          className="flex-1 bg-ticker-muted text-ticker-text border-ticker-accent"
          onKeyPress={(e) => e.key === "Enter" && handleAddAlert()}
        />
        <Button
          onClick={handleAddAlert}
          className="bg-ticker-accent hover:bg-ticker-accent/90"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-2">
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className="flex items-center gap-2 p-2 rounded bg-ticker-muted/20 animate-fade-in"
          >
            <Checkbox
              id={`alert-${alert.id}`}
              checked={alert.is_active}
              onCheckedChange={(checked) => onToggleActive(alert.id, checked as boolean)}
              className="border-ticker-accent"
            />
            <Input
              value={alert.text}
              onChange={(e) => onUpdateAlert(alert.id, e.target.value)}
              className="flex-1 bg-transparent border-ticker-muted"
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onRemoveAlert(alert.id)}
              className="text-ticker-text hover:text-red-400 hover:bg-red-400/20"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
};
